// Shared utilities for engagement budget calculations

// Rate card for budget calculation based on engagement type and university tier
export const RATE_CARD = {
  'VISITING': { 'Tier 1': 2000, 'Tier 2': 1000, 'Tier 3': 750 },
  'ADJUNCT': { 'Tier 1': 1000, 'Tier 2': 750, 'Tier 3': 500 },
  'FRACTIONAL': { 'Tier 1': 1000, 'Tier 2': 750, 'Tier 3': 500 },
  'MODULAR': { 'Tier 1': 1000, 'Tier 2': 750, 'Tier 3': 500 }
};

/**
 * Calculate total hours based on engagement type
 * @param {Object} req - The requirement/form object containing engagement details
 * @returns {number} - Total estimated hours
 */
export const calculateTotalHours = (req) => {
  const { engagement_type, event_date, end_date, start_time, end_time, duration_value, duration_unit, days_of_week, weekly_hours } = req;

  let h_per_session = 0;
  if (start_time && end_time) {
    try {
      const [h1, m1] = start_time.split(':').map(Number);
      const [h2, m2] = end_time.split(':').map(Number);
      h_per_session = Math.max(0, (h2 * 60 + m2 - (h1 * 60 + m1)) / 60);
    } catch (err) {
      h_per_session = duration_value || 0;
    }
  } else {
    h_per_session = duration_value || 0;
  }

  // MODULAR: Sessions across date range
  if (engagement_type === 'MODULAR') {
    if (event_date && end_date) {
      const d1 = new Date(event_date);
      const d2 = new Date(end_date);
      if (!isNaN(d1) && !isNaN(d2)) {
        const days = Math.floor((d2 - d1) / (1000 * 60 * 60 * 24)) + 1;
        return h_per_session * Math.max(1, days);
      }
    }
    return h_per_session;
  }

  // ADJUNCT: Count actual days based on selected days_of_week
  if (engagement_type === 'ADJUNCT') {
    if (event_date && end_date && days_of_week && days_of_week.length > 0) {
      let count = 0;
      let curr = new Date(event_date);
      const last = new Date(end_date);
      if (!isNaN(curr) && !isNaN(last)) {
        let safety = 0;
        while (curr <= last && safety < 500) {
          const dayName = curr.toLocaleDateString('en-US', { weekday: 'long' });
          if (days_of_week.includes(dayName)) {
            count++;
          }
          curr.setDate(curr.getDate() + 1);
          safety++;
        }
        return h_per_session * count;
      }
    }
    return h_per_session || 0;
  }

  // FRACTIONAL: weekly_hours * number_of_weeks
  if (engagement_type === 'FRACTIONAL') {
    if (event_date && end_date) {
      const d1 = new Date(event_date);
      const d2 = new Date(end_date);
      if (!isNaN(d1) && !isNaN(d2)) {
        const diffTime = Math.abs(d2 - d1);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        // Use ceil to ensure partial weeks are counted as full weeks for budget
        const weeks = Math.ceil((diffDays + 1) / 7);
        return (weekly_hours || 0) * Math.max(1, weeks);
      }
    }
    return (weekly_hours || 0) * 4; // Fallback to 1 month (4 weeks)
  }

  // VISITING: Handle different duration units
  if (engagement_type === 'VISITING') {
    if (duration_unit === 'Sessions') return h_per_session * (duration_value || 1);
    if (duration_unit === 'Days') return (duration_value || 1) * 8;
    if (duration_unit === 'Weeks') return (duration_value || 1) * 40;
    return duration_value || h_per_session;
  }

  return 0;
};

/**
 * Calculate budget based on hourly rate and total hours
 * @param {string} engagementType - Type of engagement (VISITING, ADJUNCT, etc.)
 * @param {string} tier - University tier (Tier 1, Tier 2, Tier 3)
 * @param {Object} formData - Form data for calculating total hours
 * @returns {number} - Calculated budget
 */
export const calculateBudget = (engagementType, tier, formData) => {
  const currentTier = tier?.trim();
  const rateInfo = RATE_CARD[engagementType];
  const hourlyRate = (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;
  const totalHours = calculateTotalHours(formData);
  return Math.round(hourlyRate * totalHours);
};

/**
 * Get hourly rate for a given engagement type and tier
 * @param {string} engagementType - Type of engagement
 * @param {string} tier - University tier
 * @returns {number} - Hourly rate
 */
export const getHourlyRate = (engagementType, tier) => {
  const currentTier = tier?.trim();
  const rateInfo = RATE_CARD[engagementType];
  return (rateInfo && currentTier) ? (rateInfo[currentTier] || 0) : 0;
};
